/**
 * Memory pairs — turn two cards, keep them if they match.
 *
 * A pair is two faces rather than two copies, so the same renderer covers plain
 * matching and the harder association version (the word "cat" matched to a
 * picture of one), which is where memory work starts feeding reading.
 */

import { z } from 'zod'
import { ColorTokenSchema, EmojiSchema, IdSchema, LabelSchema, SeedSchema, isUnique, specFor } from '../common'

export const CardFaceSchema = z.object({
  label: LabelSchema,
  emoji: EmojiSchema.optional(),
  colorToken: ColorTokenSchema.optional(),
})

export const MemoryPairSchema = z.object({
  id: IdSchema,
  a: CardFaceSchema,
  b: CardFaceSchema,
})

export const MemoryPairsConfigSchema = z
  .object({
    pairCount: z.number().int().min(3).max(12),
    /** Identical faces, or a picture matched to its word. */
    pairKind: z.enum(['identical', 'associated']),
    pairs: z.array(MemoryPairSchema).min(3).max(12),
    layout: z.object({
      columns: z.number().int().min(2).max(6),
      rows: z.number().int().min(2).max(6),
    }),
    /** How long a non-matching turn stays visible before flipping back. */
    faceDownRevealMs: z.number().int().min(400).max(5_000).default(1_200),
    shuffleSeed: SeedSchema.default(1),
    /** Say the card out loud when it turns — links the picture to the word. */
    nameCardsAloud: z.boolean().default(true),
  })
  .superRefine((config, ctx) => {
    if (config.pairs.length !== config.pairCount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pairs'],
        message: `pairCount says ${config.pairCount} but ${config.pairs.length} pairs are listed`,
      })
    }
    if (!isUnique(config.pairs.map((pair) => pair.id))) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['pairs'], message: 'pair ids must be unique' })
    }
    const cards = config.pairs.length * 2
    const slots = config.layout.columns * config.layout.rows
    if (slots !== cards) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['layout'],
        message: `${config.layout.columns}×${config.layout.rows} is ${slots} slots for ${cards} cards`,
      })
    }
    for (const [index, pair] of config.pairs.entries()) {
      const same = pair.a.label === pair.b.label && (pair.a.emoji ?? '') === (pair.b.emoji ?? '')
      if (config.pairKind === 'identical' && !same) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pairs', index],
          message: 'an identical pair must show the same face twice',
        })
      }
      if (config.pairKind === 'associated' && same) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['pairs', index],
          message: 'an associated pair needs two different faces',
        })
      }
    }
    const faceKeys = config.pairs.flatMap((pair) => [
      `${pair.a.label}|${pair.a.emoji ?? ''}`,
      `${pair.b.label}|${pair.b.emoji ?? ''}`,
    ])
    const expectedDistinct = config.pairKind === 'identical' ? config.pairs.length : config.pairs.length * 2
    if (new Set(faceKeys).size !== expectedDistinct) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['pairs'],
        message: 'two pairs share a card face, so a match would be ambiguous',
      })
    }
  })

export type MemoryPairsConfig = z.infer<typeof MemoryPairsConfigSchema>

export const MemoryPairsSpecSchema = specFor('memory_pairs', MemoryPairsConfigSchema)
